class OverviewScrollReveal extends HTMLElement {
  constructor() {
    super();
    this.lines = [];
    this.totalCount = 0;
    this.wrapper = null;
    this.activeIndex = 0;
    this.isWheeling = false;
    this.wheelTimeout = null;
    this.touchStartY = 0;
    this.handleWheel = this.handleWheel.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.lines = Array.from(this.querySelectorAll("[data-osr-line]"));
    this.totalCount = this.lines.length;
    this.setVisibleCount(0);
    this.wrapper = this.closest('[id^="osr-wrapper-"]');
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("wheel", this.handleWheel, { passive: false });

    this.addEventListener(
      "touchstart",
      (e) => {
        this.touchStartY = e.touches?.[0]?.clientY || 0;
      },
      { passive: true },
    );

    this.addEventListener(
      "touchmove",
      (e) => {
        if (!this.wrapper) return;
        const isStuck = this.isStuck();
        if (!isStuck) return;
        if (!this.touchStartY) return;
        const touchEndY = e.touches?.[0]?.clientY || 0;
        const diffY = this.touchStartY - touchEndY;
        if (Math.abs(diffY) <= 30) return;

        if (this.activeIndex === 0 && diffY < 0) return;
        if (this.activeIndex === this.totalCount && diffY > 0) return;

        e.preventDefault();
        if (!this.isWheeling) {
          const targetIndex = diffY > 0 ? this.activeIndex + 1 : this.activeIndex - 1;
          this.scrollToIndex(targetIndex, true);
          this.lockWheel();
        }
        this.touchStartY = touchEndY;
      },
      { passive: false },
    );

    this.handleScroll();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("wheel", this.handleWheel);
    if (this.wheelTimeout) window.clearTimeout(this.wheelTimeout);
  }

  setVisibleCount(count) {
    const visibleCount = Math.max(0, Math.min(this.totalCount, count));
    this.lines.forEach((el, idx) => {
      el.classList.toggle("is-visible", idx < visibleCount);
    });
  }

  isStuck() {
    if (!this.wrapper) return false;
    const top = this.wrapper.offsetTop;
    const maxScroll = this.wrapper.offsetHeight - window.innerHeight;
    const y = window.scrollY;
    return y >= top - 5 && y <= top + maxScroll + 5;
  }

  lockWheel() {
    this.isWheeling = true;
    if (this.wheelTimeout) window.clearTimeout(this.wheelTimeout);
    this.wheelTimeout = window.setTimeout(() => {
      this.isWheeling = false;
    }, 800);
  }

  scrollToIndex(index, smooth = true) {
    if (!this.wrapper) return;
    const vh = window.innerHeight;
    const target = this.wrapper.offsetTop + index * vh;
    window.scrollTo({ top: target, behavior: smooth ? "smooth" : "auto" });
  }

  handleScroll() {
    if (!this.wrapper) return;
    const scrolled = window.scrollY - this.wrapper.offsetTop;
    const vh = window.innerHeight;
    const maxScroll = this.wrapper.offsetHeight - vh;
    const clamped = Math.max(0, Math.min(maxScroll, scrolled));
    let index = Math.floor((clamped + vh / 2) / vh);
    index = Math.max(0, Math.min(this.totalCount, index));

    if (index !== this.activeIndex) {
      this.activeIndex = index;
    }
    this.setVisibleCount(this.activeIndex);
  }

  handleWheel(e) {
    if (!this.wrapper) return;
    const isStuck = this.isStuck();
    if (!isStuck) return;

    if (this.activeIndex === 0 && e.deltaY < 0) return;
    if (this.activeIndex === this.totalCount && e.deltaY > 0) return;

    e.preventDefault();
    if (this.isWheeling) return;
    const nextIndex = e.deltaY > 0 ? this.activeIndex + 1 : this.activeIndex - 1;
    this.scrollToIndex(nextIndex, true);
    this.lockWheel();
  }
}

if (!customElements.get("overview-scroll-reveal")) {
  customElements.define("overview-scroll-reveal", OverviewScrollReveal);
}
